import SearchBox from 'components/common/SearchBox';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import {
  capitalizeFirstLetter,
  convertToCurrency,
  formatDateOnly,
  showValue
} from 'utils/helpers';
import {
  ApplicationInterfaceI,
  applicationTypeOptionInterface,
  faderalState,
  OptionI,
  paramsI
} from '../../../types/interface';
import { RootState } from 'redux/store';
import { useSelector } from 'react-redux';
import * as applicationAction from '../../redux/application/applicationAction';
import { useAppDispatch } from '../../redux/HooksRedux';
import { errorNotification } from 'base/notifications/Notifications';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import {
  ACCENDING,
  APPROVED,
  DESCENDING,
  EDUCATION,
  ENGLISH,
  HEALTH,
  REJECTED,
  SCHOOL_ACTIVITY_OF_CHILD,
  SELECTED_LANGUAGE,
  UNFROZEN,
  ViewAllLimit
} from 'utils/enums/misc';
import { getCookie } from 'utils/cookieUtils';
import { StandardButton } from 'base/StandardButton/StandardButton';
import { OVERVIEW } from 'constant/RoutesUrls';
import { useNavigate } from 'react-router-dom';
import CustomAdvanceTableFooter from 'components/customComponent/CustomAdvanceTableFooter';
import { StandardLoader } from 'base/Loader/Loader';
import Badge from 'components/base/Badge';
import StandardSelect from 'base/StandardSelect/StandardSelect';
import { SortingState } from '@tanstack/react-table';
import {
  basicDataOptions,
  debounceTime,
  federal_state,
  statusOptions
} from 'utils/staticData';
import { getApplicationTypeOptionAll } from '../../redux/applicationTypeOption/applicationTypeAction';
import { getAllFederalState } from '../../redux/user/userAction';

export const Application = () => {
  const { totalPages, totalRecords } = useSelector(
    (state: RootState) => state?.applicationDataSlice || 0
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentLanguage = getCookie(SELECTED_LANGUAGE);

  const [isLoading, setIsLoading] = useState(true);
  const [applicationList, setApplicationList] = useState<
    ApplicationInterfaceI[]
  >([]);
  const [pageCount, setPageCount] = useState<number>(1);
  const [isAllVisibleData, setIsAllVisibleData] = useState(false);
  const [limit, setLimit] = useState<number>(10);
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [sorting, setSorting] = useState<SortingState>();
  const [selectedBasicData, setSelectedBasicData] = useState<string>('');
  const [applicationTypeRequestOptions, setApplicationTypeRequestOptions] =
    useState<OptionI[]>([]);
  const [applicationStateList, setApplicationStateList] = useState<OptionI[]>(
    []
  );
  const [selectedApplicationState, setSelectedApplicationState] =
    useState<string>('');
  const [selectedRequestOption, setSelectedRequestOption] = useState<
    number | undefined
  >();

  const getAllApplication = async () => {
    setIsLoading(true);
    const params: paramsI & {
      statusFilter: string;
      basic_data: string;
      request_options?: number;
      federal_state?: string;
    } = {
      page: pageCount,
      limit: isAllVisibleData ? ViewAllLimit : limit,
      keyword: searchText,
      sortBy: sorting?.[0]?.id,
      sortOrder: sorting?.[0]?.desc ? DESCENDING : ACCENDING,
      statusFilter: selectedStatus,
      basic_data: selectedBasicData,
      request_options: selectedRequestOption,
      federal_state: selectedApplicationState
    };

    try {
      const response = await dispatch(
        applicationAction.getAllApplication(params)
      );

      if (response.status === 200) {
        setApplicationList(response.data.data);
        if (pageCount > response?.data?.meta?.totalPages) {
          setPageCount(1);
        }
      } else {
        errorNotification(response.data.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedSearch = useCallback(() => {
    const handler = setTimeout(() => {
      getAllApplication();
    }, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [
    searchText,
    pageCount,
    isAllVisibleData,
    sorting,
    limit,
    selectedStatus,
    selectedBasicData,
    selectedRequestOption,
    selectedApplicationState
  ]);

  useEffect(() => {
    setIsLoading(true);
    const cleanup = debouncedSearch();
    return cleanup;
  }, [
    searchText,
    pageCount,
    isAllVisibleData,
    sorting,
    limit,
    selectedStatus,
    selectedBasicData,
    selectedRequestOption,
    selectedApplicationState
  ]);

  const previewApplication = async (
    applicationId: number,
    applicationType: string,
    basicData: string
  ) => {
    if (applicationId && applicationType && basicData) {
      navigate(
        `${OVERVIEW}?application_id=${applicationId}&application_type=${applicationType}&basic_data=${basicData}`
      );
    }
  };

  const getAllApplicationTypeRequest = async () => {
    try {
      const response = await dispatch(
        getApplicationTypeOptionAll({
          limit: 999,
          page: 1,
          keyword: '',
          is_archived: false
        })
      );
      if (response?.status === 200) {
        setApplicationTypeRequestOptions(
          response.data.request_options.map(
            (item: applicationTypeOptionInterface) => {
              return {
                label: item.name,
                value: item.application_request_id
              };
            }
          )
        );
      } else {
        errorNotification(response.data.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getFederalState = async () => {
    try {
      const response = await dispatch(
        getAllFederalState({ limit: 999, page: 1, keyword: '' })
      );
      if (response?.status === 200) {
        setApplicationStateList(
          response.data.data.map((item: faderalState) => {
            return {
              label: item.name,
              value: item.state_id
            };
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const columns: ColumnDef<ApplicationInterfaceI>[] = [
    // {
    //   id: 'index',
    //   accessorKey: t('applications.table.title_1'),
    //   enableSorting: false,
    //   header: () => (
    //     <span className="text-nowrap">{t('applications.table.title_1')}</span>
    //   ),
    //   cell: ({ row: { index } }) => <div className="py-2">{index + 1}</div>
    // },
    {
      id: 'id',
      accessorKey: t('general.text_12'),
      enableSorting: false,
      header: () => <span>{t('general.text_12')}</span>,
      cell: ({ row: { original } }) => (
        <div>{original.application_number || '--'}</div>
      )
    },
    {
      id: 'personnal_number',
      accessorKey: t('register_applicant.placeholder.personnelNumber'),
      enableSorting: false,
      header: () => {
        return (
          <span>{t('register_applicant.placeholder.personnelNumber')}</span>
        );
      },
      cell: ({ row: { original } }) => {
        return <div>{showValue(original?.user?.personnel_number)}</div>;
      }
    },
    {
      id: 'first_name',
      accessorKey: t('general.common_text.text_5'),
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_5')}</span>
      ),
      cell: ({ row: { original } }) => {
        return <div>{showValue(original?.user?.first_name)}</div>;
      }
    },
    {
      id: 'last_name',
      accessorKey: t('general.common_text.text_6'),
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_6')}</span>
      ),
      cell: ({ row: { original } }) => {
        return <div>{showValue(original?.user?.last_name)}</div>;
      }
    },
    {
      id: 'federal_state',
      accessorKey: t('basic_data.without_income.left.placeholder.federalState'),
      enableSorting: false,
      header: () => {
        return (
          <span>
            {t('basic_data.without_income.left.placeholder.federalState')}
          </span>
        );
      },
      cell: ({ row: { original } }) => {
        return (
          <div>
            {showValue(
              applicationStateList.find(
                state =>
                  state.value ===
                  (original?.federal_state_id ||
                    original?.user?.federal_state_id)
              )?.label
            )}
          </div>
        );
      }
    },
    {
      id: 'application_type',
      accessorKey: t('applications.table.title_2'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('applications.table.title_2')}</span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <div>
            <ConditionalRender condition={original.application_type === HEALTH}>
              {t('applications.application_type.text_1')}
            </ConditionalRender>
            <ConditionalRender
              condition={
                original.application_type.toLowerCase() ===
                EDUCATION.toLowerCase()
              }
            >
              {t('applications.application_type.text_2')}
            </ConditionalRender>
          </div>
        );
      }
    },
    // {
    //   id: "refundable_amount",
    //   accessorKey: t('applications.table.title_6'),
    //   header: () => (
    //     <span>{t('applications.table.title_6')}</span>
    //   ),
    //   cell: ({row : {original}}) => {
    //     if (original?.health?.refundable_amount) {
    //       return <div>{convertToCurrency(original?.health?.refundable_amount || "")}</div>;
    //     } else if (original?.education?.total_refund_amount) {
    //       return <div>{convertToCurrency(original?.education?.total_refund_amount || "")}</div>;
    //     } else {
    //       return <div>--</div>;
    //     }
    //   },
    //   enableSorting: false
    // },
    {
      id: 'request_options',
      accessorKey: t('applications.table.title_5'),
      enableSorting: false,
      header: () => {
        return <span>{t('applications.table.title_5')}</span>;
      },
      cell: ({ row: { original } }) => {
        return (
          <div>
            {applicationTypeRequestOptions.filter(
              i => i.value === original.request_options
            )[0]?.label || '--'}
          </div>
        );
      }
    },
    {
      id: 'current_level',
      accessorKey: t('applications.table.title_7'),
      enableSorting: false,
      header: () => {
        return <span>{t('applications.table.title_7')}</span>;
      },
      cell: ({ row: { original } }) => {
        return (
          <div>
            {(currentLanguage === ENGLISH
              ? original?.level_name_en
              : original?.level_name_de) || '--'}
          </div>
        );
      }
    },
    {
      id: 'responsible_person',
      accessorKey: t('applications.table.title_8'),
      enableSorting: false,
      header: () => {
        return <span>{t('applications.table.title_8')}</span>;
      },
      cell: ({ row: { original } }) => {
        return <div>{original?.responsible_person_name || '--'}</div>;
      }
    },
    {
      id: 'submission_date',
      accessorKey: t('general.common_text.text_22'),
      header: () => {
        return <span>{t('general.common_text.text_22')}</span>;
      },
      cell: ({ row: { original } }) => {
        return <div>{formatDateOnly(original.submitted_on)}</div>;
      }
    },
    {
      id: 'application_status',
      accessorKey: t('general.common_text.text_7'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_7')}</span>
      ),
      cell: ({ row: { original } }) => {
        switch (original.application_status) {
          case APPROVED:
            return (
              <Badge variant="phoenix" bg={'success'}>
                {t('applications.status.text_1')}
              </Badge>
            );
          case REJECTED:
            return (
              <Badge variant="phoenix" bg={'danger'}>
                {t('applications.status.text_2')}
              </Badge>
            );
          case UNFROZEN:
            return (
              <Badge variant="phoenix" bg={'secondary'}>
                {t('applications.status.text_4')}
              </Badge>
            );
          default:
            return (
              <Badge variant="phoenix" bg={'warning'}>
                {t('applications.status.text_3')}
              </Badge>
            );
        }
      }
    },
    {
      id: 'action',
      accessorKey: t('general.common_text.text_8'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_8')}</span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <div className="d-flex align-items-start justify-content-between">
            {/* <img src="./svgs/icon-approve.svg" alt="" className="icon-approve" />
            <img src="./svgs/icon-reject.svg" className="icon-reject me-2" alt="" /> */}
            <StandardButton
              className="add-btn-style px-1 "
              buttonText={t('applications.action.text_1')}
              onClick={() => {
                previewApplication(
                  original?.master_application_id,
                  original?.application_type,
                  original?.basic_data
                );
              }}
            />
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    getAllApplicationTypeRequest();
    getFederalState();
  }, []);

  const table = useAdvanceTable({
    data: applicationList || [],
    columns: columns,
    selection: false,
    sortable: true,
    pageSize: limit,
    pagination: true,
    sorting: sorting,
    setSorting: setSorting
  });

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between mb-2 align-items-center flex-column flex-md-row gap-3">
        <div className="w-auto">
          <h3 className="m-0">
            {capitalizeFirstLetter(t('header.text.text_5'))}{' '}
          </h3>
        </div>
        <div className="d-flex gap-2 w-auto flex-column flex-md-row">
          <SearchBox
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <StandardSelect
            formClassName="w-330"
            id=""
            onChange={e => {
              setSelectedStatus(e);
            }}
            options={statusOptions}
            placeholder={t('applications.placeholder.text_1')}
            showError={false}
            validations={[]}
            value={selectedStatus}
            label=""
            isMulti={false}
            isClearable={true}
          />
          <StandardSelect
            formClassName="w-330"
            id=""
            label={''}
            onChange={(e: string | number) =>
              setSelectedRequestOption(e as number)
            }
            options={applicationTypeRequestOptions}
            placeholder={t('product.placeholder.text_1')}
            showError={false}
            validations={[]}
            value={selectedRequestOption}
            isClearable={true}
          />
          <StandardSelect
            formClassName="w-330"
            id=""
            label={''}
            onChange={(e: string | number) =>
              setSelectedApplicationState(e as string)
            }
            options={applicationStateList}
            placeholder={t('product.placeholder.text_4')}
            showError={false}
            validations={[]}
            value={selectedApplicationState}
            isClearable={true}
          />
        </div>
      </div>
      <div>
        <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
          <div className="px-4 py-1 px-lg-6 bg-body-emphasis overflow-auto scrollbar rounded border-top border-bottom border-left border-right border position-relative top-1">
            <AdvanceTableProvider {...table}>
              <AdvanceTable
                tableProps={{
                  className: 'phoenix-table fs-9 p-0',
                  responsive: true
                }}
                dataLength={applicationList.length}
              />
              <ConditionalRender condition={totalPages > 0}>
                <CustomAdvanceTableFooter
                  pagination
                  totalPages={totalPages}
                  totalRecords={totalRecords}
                  recordsPerPage={limit}
                  currentPage={pageCount}
                  setCurrentPage={setPageCount}
                  CanPreviousPage={() => pageCount > 1}
                  CanNextPage={() => totalPages > pageCount}
                  handleNextPage={() => setPageCount(pageCount + 1)}
                  handlePreviousPage={() => setPageCount(pageCount - 1)}
                  showViewAllBtn={true}
                  isAllVisibleData={isAllVisibleData}
                  setIsAllVisibleData={setIsAllVisibleData}
                  setRecordsPerPage={setLimit}
                  showDataPerPage={totalPages > 1}
                  isLoading={isLoading}
                />
              </ConditionalRender>
            </AdvanceTableProvider>
          </div>
        </ConditionalRender>
      </div>
    </>
  );
};
